'use client';

import { useMemo, useEffect, useState } from 'react';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import TokenPoolCard from '@/components/global/TokenPoolCard';
import { Image } from '@/components/shared/Image';
import { H2, P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type T_CarouselApi
} from '@/components/ui/carousel';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { getPresaleEndMs } from '@/lib/utils/getPresaleEndMs';
import { getPoolsLinkHref } from '@/lib/utils/pools';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';
import { T_TokenPool } from '@/ts/types/T_Profile';

const MorePoolsContent = () => {
  const t = useTranslations('profile.morePools');
  const pools = useGlobalData('pools');

  const poolsLink = getPoolsLinkHref(pools, {
    fallbackPresaleHref:
      C_Routes[E_Routes.POOLS_PRESALE]?.link || '/pools-presale'
  });

  return (
    <div className="flex flex-col justify-center items-center gap-4 h-full text-center">
      <H2 className="text-center">{t('title')}</H2>
      <P className="text-center">{t('description')}</P>
      <Button asChild variant="default" className="uppercase">
        <Link href={poolsLink}>{t('button')}</Link>
      </Button>
    </div>
  );
};

const TokenPoolsSlider = ({ data }: { data: T_TokenPool[] }) => {
  const { isMobile } = useDeviceDetection();

  // Check if any pool has an active presale
  const hasAnyActivePresale = useMemo(() => {
    return data.some(pool => {
      const presaleEndMs = getPresaleEndMs(pool.presaleEndDate);
      if (presaleEndMs === null) return false;
      const isPresaleEnded = new Date(presaleEndMs) < new Date();
      return !isPresaleEnded;
    });
  }, [data]);
  const [api, setApi] = useState<T_CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateScrollState = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateScrollState();
    api.on('reInit', updateScrollState);
    api.on('select', updateScrollState);

    return () => {
      api.off('reInit', updateScrollState);
      api.off('select', updateScrollState);
    };
  }, [api]);

  if (isMobile) {
    return (
      <div className="flex overflow-auto gap-4 left-0 absolute w-screen">
        <div className="flex gap-4 overflow-x-auto w-full px-4">
          {data.map((pool, index: number) => (
            <div key={index} className="min-w-70 shrink-0">
              <TokenPoolCard
                pool={pool}
                hasAnyActivePresale={hasAnyActivePresale}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
  // When there's only 1 item, show it at 1/2 width with "More Pools" content
  if (data.length === 1) {
    return (
      <div className="flex gap-4 ">
        <div className="w-1/2 shrink-0">
          <TokenPoolCard
            pool={data[0]}
            hasAnyActivePresale={hasAnyActivePresale}
          />
        </div>
        <div className="w-1/2 shrink-0">
          <MorePoolsContent />
        </div>
      </div>
    );
  }

  // When there are 2 items, show both at 1/2 width each
  if (data.length === 2) {
    return (
      <div className="flex gap-4">
        {data.map((pool, index: number) => (
          <div key={index} className="w-1/2 shrink-0">
            <TokenPoolCard
              pool={pool}
              hasAnyActivePresale={hasAnyActivePresale}
            />
          </div>
        ))}
      </div>
    );
  }

  // When there are 3 items, show all 3 without carousel
  // if (data.length === 3) {
  //   return (
  //     <div className="flex gap-4 left-0 w-screen">
  //       {data.map((pool, index: number) => (
  //         <div key={index} className="flex-shrink-0 md:basis-88">
  //           <TokenPoolCard pool={pool} />
  //         </div>
  //       ))}
  //     </div>
  //   );
  // }

  // When there are more than 3 items, use the carousel
  return (
    <div className="flex overflow-auto gap-4 left-0 absolute w-screen">
      <Carousel
        className="w-full relative"
        opts={{
          align: 'start',
          slidesToScroll: 1
        }}
        setApi={setApi}
      >
        <div className="hidden lg:block absolute left-0 top-0 bottom-0 xl:w-[40%] w-[40%] z-10 pointer-events-none">
          <Image
            src="/assets/images/sliderMask.png"
            alt="Slider mask start"
            fill
            className="object-cover rotate-180"
          />
        </div>

        <div className="hidden lg:block absolute right-0 top-0 bottom-0 xl:w-[40%] w-[40%] z-10 pointer-events-none">
          <Image
            src="/assets/images/sliderMask.png"
            alt="Slider mask end"
            fill
            className="object-cover"
          />
        </div>

        <CarouselContent className="-ml-4">
          <div className="min-w-0 shrink-0 grow-0 pl-4 basis-(--slider-offset)" />
          {data.map((pool, index: number) => (
            <CarouselItem key={index} className="pl-4 basis-70 md:basis-88">
              <TokenPoolCard
                pool={pool}
                hasAnyActivePresale={hasAnyActivePresale}
              />
            </CarouselItem>
          ))}
          <div className="min-w-0 shrink-0 grow-0 pl-4 basis-(--slider-offset)" />
        </CarouselContent>

        {canScrollPrev && (
          <CarouselPrevious className="-ml-4.5 left-6! xl:left-(--slider-offset)! bg-foreground/10 backdrop-blur-sm border-foreground/20 text-foreground hover:bg-foreground/20 hover:text-foreground z-20 " />
        )}
        {canScrollNext && (
          <CarouselNext className="-mr-4.5 right-6! xl:right-(--slider-offset)! bg-foreground/10 backdrop-blur-sm border-foreground/20 text-foreground hover:bg-foreground/20 hover:text-foreground z-20 " />
        )}
      </Carousel>
    </div>
  );
};

export default TokenPoolsSlider;
